import { normalizeFrenchCharacters, removeHyphens } from "../common/utils/PuzzlePlayLogic";

const removeSpacesCompact = (word) => {
    if (!word || typeof word !== "string") return "";
    return word.replace(/\s+/g, "");
};

/** Normalize grid / API direction to `horizontal` | `vertical` for clue-key matching. */
const normCrossPlayDirection = (d) => {
    if (d === "horizontal" || d === "vertical") return d;
    const s = String(d || "").toLowerCase();
    if (s === "across" || s === "horizontal" || s === "h") return "horizontal";
    if (s === "down" || s === "vertical" || s === "v") return "vertical";
    return null;
};

export const crossMeaningClueKey = (num, dir) => {
    const d = normCrossPlayDirection(dir);
    if (num == null || d == null) return null;
    return `${num}-${d}`;
};

/** Keys for matching `filledCoordinates` text to `/words-with-meanings` entries (spaces, accents, hyphens). */
export const crossMeaningKeyVariantsForString = (raw) => {
    if (!raw || typeof raw !== "string") return [];
    const t = raw.toLowerCase().trim();
    if (!t) return [];
    const noHyphen = removeHyphens(t);
    const noSpace = removeSpacesCompact(t);
    const noSpaceNoHyphen = removeHyphens(noSpace);
    const fr = normalizeFrenchCharacters(t);
    const frNoSpace = removeSpacesCompact(fr);
    const frNoHyphen = removeHyphens(fr);
    const frCompact = removeHyphens(removeSpacesCompact(fr));
    return [...new Set([t, noHyphen, noSpace, noSpaceNoHyphen, fr, frNoSpace, frNoHyphen, frCompact].filter(Boolean))];
};

/** Same part-of-speech object shape as `CrossWordCluesPanel` / dictionary cache (not array of strings). */
export const objectMeaningToPreviewLine = (meaningObj) => {
    if (!meaningObj || typeof meaningObj !== "object" || Array.isArray(meaningObj)) return "";
    const keys = Object.keys(meaningObj).filter((key) => key !== "title");
    if (keys.length === 0) return "";
    const partOfSpeechOrder = ["verbe", "verb", "nom", "noun", "adjectif", "adjective", "adverbe", "adverb"];
    const sortedKeys = keys.sort((a, b) => {
        const indexA = partOfSpeechOrder.indexOf(a.toLowerCase());
        const indexB = partOfSpeechOrder.indexOf(b.toLowerCase());
        return (indexA === -1 ? 999 : indexA) - (indexB === -1 ? 999 : indexB);
    });
    const firstKey = sortedKeys[0];
    const fullDefinition = meaningObj[firstKey];
    if (!fullDefinition || typeof fullDefinition !== "string") return "";
    let firstMeaning = fullDefinition.trim();
    const startIndex = firstMeaning.indexOf("1.");
    if (startIndex === -1) {
        const firstPeriod = firstMeaning.indexOf(".");
        if (firstPeriod !== -1) {
            firstMeaning = firstMeaning.substring(0, firstPeriod + 1).trim();
        }
    } else {
        const afterOne = firstMeaning.substring(startIndex + 2).trim();
        const nextTwoIndex = afterOne.indexOf("2.");
        if (nextTwoIndex !== -1) {
            firstMeaning = afterOne.substring(0, nextTwoIndex).trim();
            if (!firstMeaning.endsWith(".")) firstMeaning = `${firstMeaning.trim()}.`;
        } else {
            firstMeaning = afterOne.trim();
            if (!firstMeaning.endsWith(".")) firstMeaning = `${firstMeaning.trim()}.`;
        }
    }
    return firstMeaning;
};

/** Normalize `meaning` from `/words-with-meanings`: string[], "pos: def" strings, plain string, or POS object. */
export const meaningToPreviewText = (meaning) => {
    if (meaning == null) return "";
    if (typeof meaning === "string") {
        const t = meaning.trim();
        if (!t) return "";
        const colonIndex = t.indexOf(": ");
        return colonIndex > 0 ? t.substring(colonIndex + 2).trim() : t;
    }
    if (Array.isArray(meaning)) {
        const strings = meaning.filter((x) => typeof x === "string" && x.trim());
        if (strings.length === 0) return "";
        const first = strings[0];
        const colonIndex = first.indexOf(": ");
        return colonIndex > 0 ? first.substring(colonIndex + 2).trim() : first.trim();
    }
    if (typeof meaning === "object") {
        return objectMeaningToPreviewLine(meaning);
    }
    return "";
};

export const lookupCrossWordMeaningPreview = (
    wordMap,
    clueMap,
    coordText,
    coordDisplayWord,
    wordNumber,
    direction
) => {
    const tryEntry = (entry) => {
        if (!entry) return "";
        return meaningToPreviewText(entry.meaning) || "";
    };

    const keys = new Set([
        ...crossMeaningKeyVariantsForString(coordText || ""),
        ...crossMeaningKeyVariantsForString(
            coordDisplayWord != null && String(coordDisplayWord).trim() !== "" ? String(coordDisplayWord) : ""
        ),
    ]);

    for (const k of keys) {
        const line = tryEntry(wordMap[k]);
        if (line) return line;
    }

    const ck = crossMeaningClueKey(wordNumber, direction);
    if (ck && clueMap[ck]) {
        return tryEntry(clueMap[ck]) || "";
    }

    return "";
};

/**
 * @param {Array<{ number?: number, word?: string, wordNorm?: string, direction?: string, meaning?: unknown }>} wordsWithMeanings
 * @returns {{ wordMap: Record<string, { meaning: unknown }>, clueMap: Record<string, { meaning: unknown }> }}
 */
export function buildCrossMeaningsMapsFromWordsWithMeanings(wordsWithMeanings) {
    const wordMap = {};
    const clueMap = {};
    if (!Array.isArray(wordsWithMeanings) || wordsWithMeanings.length === 0) {
        return { wordMap, clueMap };
    }
    wordsWithMeanings.forEach((item) => {
        const entry = { meaning: item.meaning };
        const variantKeys = new Set([
            ...crossMeaningKeyVariantsForString(item.word || ""),
            ...crossMeaningKeyVariantsForString(item.wordNorm || ""),
        ]);
        variantKeys.forEach((k) => {
            wordMap[k] = entry;
        });
        const ck = crossMeaningClueKey(item.number, item.direction);
        if (ck) {
            clueMap[ck] = entry;
        }
    });
    return { wordMap, clueMap };
}

/**
 * Curated clues from GET /home/cross/:id `displayClue` (`{ word, definition }[]`) — single preview line per word (no dictionary/Wiktionary).
 * @param {Array<{ word?: string, definition?: string }>} displayClue
 * @returns {{ wordMap: Record<string, { meaning: string }>, clueMap: Record<string, never> }}
 */
export function buildCrossMeaningsMapsFromDisplayClue(displayClue) {
    const wordMap = {};
    const clueMap = {};
    if (!Array.isArray(displayClue) || displayClue.length === 0) {
        return { wordMap, clueMap };
    }
    displayClue.forEach((item) => {
        const def = String(item?.definition ?? "").trim();
        const w = item?.word;
        if (!w || !def) return;
        const entry = { meaning: def };
        crossMeaningKeyVariantsForString(String(w)).forEach((k) => {
            wordMap[k] = entry;
        });
    });
    return { wordMap, clueMap };
}
